// @ts-nocheck
import React from 'react';
import classnames from 'classnames';
import type { BuildColumnsProps } from './types';
import Card from './Card';
import styles from './index.module.less';
import Element from '../../../core/Element';
import { Type, Color, FontType, Align } from '../../../core/enums';
import { EventTracking } from '../../../utilities/withEventTracking';
import { BackgroundImage } from '../../atoms/background-image';
import { useBreakpoint } from '../../styleless/breakpoint';
import { Typography } from '../../archived/typography';

export const isEven = (num: number): boolean => num % 2 === 0;

const BuildColumns = ({
  colorChoices = [],
  legend,
  ariaLabelledby,
  selectedIndex,
  isExpanded,
  onChange,
  forceColumn,
  tools,
  type,
  qeID: containerQeID,
}: BuildColumnsProps): React.ReactElement => {
  const { lg } = useBreakpoint();
  const colorCount = colorChoices.length;
  const getTertiaryWrapperClass = !forceColumn
    ? styles.parentWrapperTertiaryRow
    : styles.parentWrapperTertiaryColumn;

  const colorChoiceElements = colorChoices.map((colorObject, i) => {
    const {
      colorValueOne,
      colorValueTwo,
      name,
      id,
      qeId,
      backgroundImage,
      miniCardArt,
    } = colorObject;
    const isLast = i === colorCount - 1;
    const isSelected = i === selectedIndex;
    const borderClass = isSelected
      ? styles.selectedBorder
      : styles.unselectedBorder;
    const tertiaryStyleClass = isSelected
      ? styles.selectedTertiaryCard
      : styles.defaultTertiaryCard;
    const topLevelClass = classnames(borderClass, {
      [styles.colorContainer]: isExpanded,
      [styles.colorContainerCompact]: !isExpanded,
    });
    const getColumnClass = (forceColumn: boolean) => {
      return `${styles.cardBorder} ${!forceColumn && styles.compactColumn}`;
    };

    const handleSelect = (event: any) => onChange(i, id, event);

    const getColumn = () => {
      if (type === Type.Primary) {
        return (
          <Element
            key={id}
            className={getColumnClass(forceColumn)}
            theme={{
              marginRight: !isLast && !forceColumn ? 5 : 0,
              bgColor: Color.Gray01,
            }}
          >
            <Card
              colorValueOne={colorValueOne}
              colorValueTwo={colorValueTwo}
              name={name}
              id={id}
              qeId={qeId}
              containerQeID={containerQeID}
              onClick={handleSelect}
              isLast={isLast}
              forceColumn={forceColumn}
              isExpanded={isExpanded}
              elementClassNames={topLevelClass}
              isSelected={isSelected}
              tools={
                tools || {
                  tracking: {
                    methods: ['click'],
                    element: containerQeID
                      ? `${containerQeID}:ColorPicker`
                      : 'ColorPicker',
                    details: [name],
                  },
                }
              }
            />
          </Element>
        );
      } else if (type === Type.Secondary) {
        // Circle Variant.
        return (
          <EventTracking
            key={id}
            tools={
              tools || {
                tracking: {
                  methods: ['click'],
                  element: containerQeID
                    ? `${containerQeID}:ColorPicker`
                    : 'ColorPicker',
                  details: [name],
                },
              }
            }
          >
            {({ onClick }) => (
              <Element
                Component="label"
                htmlFor={qeId}
                qeId={`${qeId}_label`}
                className={classnames(
                  styles.secondaryTypeLabel,
                  isSelected ? styles.variantLabelSelected : styles.variantLabel
                )}
                theme={{
                  marginLeft: 5,
                  marginRight: 5,
                }}
              >
                <input
                  type="radio"
                  id={qeId}
                  aria-describedby={legend.id ? legend.id : ''}
                  // if ariaLabelledby exist and the radiobutton is not selected we need
                  // to show for the sr aria-label. In other case the sr will read ariaLabelledby
                  // with the same value for the all radio-buttons.
                  aria-labelledby={
                    isSelected && ariaLabelledby ? ariaLabelledby : ''
                  }
                  aria-label={
                    !ariaLabelledby || (!isSelected && ariaLabelledby)
                      ? name
                      : ''
                  }
                  data-qe-id={`${qeId}_input`}
                  className={styles.secondaryTypeInputHidden}
                  name={containerQeID}
                  checked={isSelected}
                  onChange={() => {}}
                  onClick={(event) => {
                    handleSelect(event);
                    onClick();
                  }}
                />
                <Element
                  Component="span"
                  aria-hidden="true"
                  className={styles.isNoCSS}
                >
                  {name}
                </Element>
                <BackgroundImage
                  imageUrl={backgroundImage}
                  height={36}
                  className={styles.circleImage}
                />
              </Element>
            )}
          </EventTracking>
        );
      } else if (type === Type.Tertiary) {
        // Type Tertiary: card img + card name.
        return (
          <EventTracking
            key={id}
            tools={
              tools || {
                tracking: {
                  methods: ['click'],
                  element: containerQeID
                    ? `${containerQeID}:ColorPicker`
                    : 'ColorPicker',
                  details: [name],
                },
              }
            }
          >
            {({ onClick }) => (
              <div
                aria-describedby={legend.id ? legend.id : ''}
                className={classnames(
                  !forceColumn
                    ? styles.cardWrapperTertiary
                    : styles.cardWrapperTertiaryColumn,
                  styles.cardBorder,
                  tertiaryStyleClass
                )}
              >
                <input
                  id={qeId}
                  type="radio"
                  onClick={(event: any) => {
                    handleSelect(event);
                    onClick();
                  }}
                  data-qe-id={`${qeId}_input`}
                  className={`${styles.tertiaryColorPickerBtn} ${styles.hidden}`}
                />
                <Element
                  Component="label"
                  htmlFor={qeId}
                  aria-label={name}
                  qeId={`${qeId}_label`}
                  className={forceColumn ? styles.contentFlexWrapper : ''}
                  theme={{
                    marginLeft: forceColumn ? 3 : 'auto',
                    marginRight: forceColumn ? 3 : 'auto',
                    marginTop: forceColumn ? 3 : 10,
                    marginBottom: forceColumn ? 3 : 7,
                  }}
                >
                  <BackgroundImage
                    imageUrl={forceColumn ? backgroundImage : miniCardArt}
                    height={forceColumn ? 36 : 50}
                    className={
                      forceColumn ? styles.circleImage : styles.cardArtImage
                    }
                  />
                  <Typography
                    $size={lg ? 15 : 16}
                    theme={{
                      textHeading: 4,
                      margin: 0,
                      paddingLeft: 5,
                      paddingRight: 5,
                      color: isSelected ? Color.BrightBlue : Color.Gray05,
                    }}
                    $family={
                      isSelected
                        ? FontType.HelveticaBold
                        : FontType.HelveticaRegular
                    }
                  >
                    {name}
                  </Typography>
                </Element>
              </div>
            )}
          </EventTracking>
        );
      }
    };

    return getColumn();
  });

  return (
    <>
      {containerQeID ? (
        <fieldset
          className={classnames({
            [styles.compactRow]: !forceColumn && type !== Type.Secondary,
            [styles.secondaryTypeFieldset]: type === Type.Secondary,
            [styles.alignLeft]: legend && legend.direction === Align.Left,
          })}
        >
          {legend && legend.name && (
            <Element
              Component="legend"
              className={classnames({
                [styles.legendTitle]: legend.showAsTitle,
              })}
              theme={legend.theme}
            >
              {legend.name}
            </Element>
          )}
          {colorChoiceElements}
        </fieldset>
      ) : (
        <div
          className={classnames(
            'buttonWrapper',
            !forceColumn ? styles.compactRow : '',
            type === Type.Tertiary && getTertiaryWrapperClass
          )}
          role="radiogroup"
        >
          {colorChoiceElements}
        </div>
      )}
    </>
  );
};

export default BuildColumns;
